import { useEffect, useMemo, useState } from 'react';
import { Card, DataTable, InlineStack, Pagination, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { Delta } from './Delta';
import { businessUnitParam } from './BusinessUnitFilter';
import { formatInteger, formatPercent, getYears, sumBy, toNumber } from './analyticsHelpers';

// Cost category breakdown (BestellingType on incoming invoices) for one year at a time.
export default function CategoryBreakdownCard({ businessUnitId = null }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const result = await getRequest(`/api/metrics/costs-by-category${businessUnitParam(businessUnitId, true)}`);
      if (cancelled) return;
      setData(Array.isArray(result) ? result : []);
      setLoading(false);
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  const years = useMemo(() => getYears(data), [data]);
  const selectedYear = year && years.includes(year) ? year : years[years.length - 1];

  const { rows, yearTotal, prevTotals } = useMemo(() => {
    const currentRows = data.filter((row) => Number(row.year) === Number(selectedYear)).sort((a, b) => toNumber(b.total) - toNumber(a.total));
    const previousRows = data.filter((row) => Number(row.year) === Number(selectedYear) - 1);
    const previousByCategory = {};
    previousRows.forEach((row) => {
      previousByCategory[row.category] = toNumber(row.total);
    });
    return {
      rows: currentRows,
      yearTotal: sumBy(currentRows, 'total'),
      prevTotals: previousByCategory
    };
  }, [data, selectedYear]);

  const tableRows = rows.map((row) => {
    const total = toNumber(row.total);
    return [
      row.category,
      formatInteger(row.invoice_count),
      formatToCurrency(total),
      yearTotal ? formatPercent(total / yearTotal) : '—',
      <Delta current={total} previous={prevTotals[row.category]} invert />
    ];
  });

  const totals = ['', formatInteger(sumBy(rows, 'invoice_count')), formatToCurrency(yearTotal), yearTotal ? formatPercent(1) : '—', ''];

  const yearIndex = years.indexOf(selectedYear);

  return (
    <Card>
      <CardHeading title={`Costs per category — ${selectedYear || ''}`} />
      <Text tone="subdued" as="p">
        Incoming invoice amounts as allocated to cost categories via project allocations (same source as the Cost Category Distribution graph). Invoices without an allocation are not included.
      </Text>
      <div style={{ margin: '12px 0' }}>
        <InlineStack align="end">
          <Pagination
            hasPrevious={yearIndex > 0}
            hasNext={yearIndex < years.length - 1}
            onPrevious={() => setYear(years[yearIndex - 1])}
            onNext={() => setYear(years[yearIndex + 1])}
            label={<b>{selectedYear}</b>}
          />
        </InlineStack>
      </div>
      {loading ? (
        <SkeletonBodyText lines={8} />
      ) : (
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          stickyHeader
          columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
          headings={[<b>Category</b>, <b>Invoices</b>, <b>Allocated amount</b>, <b>Share</b>, <b>Δ vs prev. year</b>]}
          rows={tableRows}
          totals={totals}
          showTotalsInFooter
        />
      )}
    </Card>
  );
}
